export type AthleteId = string;

export interface Competitor {
  id: AthleteId;
  name: string;
  gender: 'male' | 'female';
}

export interface RunResult {
  time: number | null; // seconds with 2 decimal places
  dnf: boolean;
  na: boolean;  // fell / infinite time — tie-broken by qual rank
}

export interface QualSlot {
  timeRange: string;
  leftId: AthleteId | null;
  rightId: AthleteId | null;
  leftResult: RunResult;
  rightResult: RunResult;
}

export interface FinalsMatch {
  timeRange: string;
  matchLabel: string;  // e.g. "T1 – T16"
  resultLabel: string; // e.g. "W1" or "Gold"
  leftId: AthleteId | null;
  rightId: AthleteId | null;
  leftResult: RunResult;
  rightResult: RunResult;
}

export interface SpeedCompetitionData {
  competitors: Competitor[];
  qualRound1: QualSlot[];
  qualRound2: QualSlot[];
  // Men's bracket: QF → SF → Finals
  mensQuarterFinals: FinalsMatch[];
  mensSemiFinals: FinalsMatch[];
  mensFinals: FinalsMatch[];
  // Women's bracket: SF → Finals
  womensSemiFinals: FinalsMatch[];
  womensFinals: FinalsMatch[];
}

// ─── helpers ────────────────────────────────────────────────────────────────

const emptyResult = (): RunResult => ({ time: null, dnf: false, na: false });

const makeQS = (timeRange: string, leftId: string | null, rightId: string | null): QualSlot => ({
  timeRange, leftId, rightId, leftResult: emptyResult(), rightResult: emptyResult(),
});

const makeFM = (timeRange: string, matchLabel: string, resultLabel: string): FinalsMatch => ({
  timeRange, matchLabel, resultLabel,
  leftId: null, rightId: null,
  leftResult: emptyResult(), rightResult: emptyResult(),
});

// ─── default data ────────────────────────────────────────────────────────────

export const DEFAULT_COMPETITORS: Competitor[] = [
  { id: 'm1',  name: 'Matej Arh',          gender: 'male'   },
  { id: 'm2',  name: 'Frederik Leys',       gender: 'male'   },
  { id: 'm3',  name: 'David Kammerer',      gender: 'male'   },
  { id: 'm6',  name: 'Aldrik Bethke',       gender: 'male'   },
  { id: 'm7',  name: 'Kevin Leveque',       gender: 'male'   },
  { id: 'm9',  name: 'Jamie Barendrecht',   gender: 'male'   },
  { id: 'm10', name: 'Matthew Styles-West', gender: 'male'   },
  { id: 'f1',  name: 'Manca Smrekar',       gender: 'female' },
  { id: 'f3',  name: 'Tatjana Nußer',       gender: 'female' },
  { id: 'f4',  name: 'Crystal Martin',      gender: 'female' },
  { id: 'f6',  name: 'Nat Vorel',           gender: 'female' },
  { id: 'f7',  name: 'Beki Roberts',        gender: 'female' },
];

export const STORAGE_KEY = 'speed-competition-2026';

export function getDefaultData(): SpeedCompetitionData {
  return {
    competitors: DEFAULT_COMPETITORS.map(c => ({ ...c })),

    // ── Round 1 qualifications ────────────────────────────────────────────
    qualRound1: [
      makeQS('16:20 - 16:22', 'm1',  'm9'),   // Arh vs Barendrecht
      makeQS('16:22 - 16:24', 'm2',  'm7'),   // Leys vs Leveque
      makeQS('16:24 - 16:26', 'm3',  'm6'),   // Kammerer vs Bethke
      makeQS('16:26 - 16:28', 'm10', 'f1'),   // Styles-West vs Smrekar
      makeQS('16:28 - 16:30', 'f3',  'f7'),   // Nußer vs Roberts
      makeQS('16:30 - 16:32', 'f4',  'f6'),   // Martin vs Vorel
    ],

    // ── Round 2 qualifications ────────────────────────────────────────────
    qualRound2: [
      makeQS('16:40 - 16:42', 'm1',  'm6'),   // Arh vs Bethke
      makeQS('16:42 - 16:44', 'm2',  'm9'),   // Leys vs Barendrecht
      makeQS('16:44 - 16:46', 'm3',  'm10'),  // Kammerer vs Styles-West
      makeQS('16:46 - 16:48', 'm7',  'f1'),   // Leveque vs Smrekar
      makeQS('16:48 - 16:50', 'f4',  'f7'),   // Martin vs Roberts
      makeQS('16:50 - 16:52', 'f3',  'f6'),   // Nußer vs Vorel
    ],

    // ── Men's bracket ─────────────────────────────────────────────────────
    mensQuarterFinals: [
      makeFM('17:15 - 17:18', 'MT1 – MT8', 'MQ1'),
      makeFM('17:18 - 17:21', 'MT2 – MT7', 'MQ2'),
      makeFM('17:21 - 17:24', 'MT3 – MT6', 'MQ3'),
      makeFM('17:24 - 17:27', 'MT4 – MT5', 'MQ4'),
    ],
    mensSemiFinals: [
      makeFM('17:40 - 17:43', 'MQ2 – MQ3', 'MF1 / ML1'),
      makeFM('17:43 - 17:46', 'MQ1 – MQ4', 'MF2 / ML2'),
    ],
    mensFinals: [
      makeFM('18:05 - 18:07', 'ML1 – ML2', '🥉 Bronze M'),
      makeFM('18:07 - 18:09', 'MF1 – MF2', '🥇 Gold M'),
    ],

    // ── Women's bracket ───────────────────────────────────────────────────
    womensSemiFinals: [
      makeFM('17:30 - 17:33', 'WT1 – WT4', 'WF1 / WL1'),
      makeFM('17:33 - 17:36', 'WT2 – WT3', 'WF2 / WL2'),
    ],
    womensFinals: [
      makeFM('18:00 - 18:03', 'WL1 – WL2', '🥉 Bronze W'),
      makeFM('18:03 - 18:06', 'WF1 – WF2', '🥇 Gold W'),
    ],
  };
}

// ─── schedule generation ──────────────────────────────────────────────────────

const R1_SLOTS = [
  '16:20 - 16:22', '16:22 - 16:24', '16:24 - 16:26', '16:26 - 16:28',
  '16:28 - 16:30', '16:30 - 16:32', '16:32 - 16:34', '16:34 - 16:36',
];
const R2_SLOTS = [
  '16:40 - 16:42', '16:42 - 16:44', '16:44 - 16:46', '16:46 - 16:48',
  '16:48 - 16:50', '16:50 - 16:52', '16:52 - 16:54', '16:54 - 16:56',
];

export function generateQualSchedule(
  competitors: Competitor[]
): Pick<SpeedCompetitionData, 'qualRound1' | 'qualRound2'> {
  // Split by gender — men and women never climb against each other
  const males   = competitors.filter(c => c.gender === 'male');
  const females = competitors.filter(c => c.gender === 'female');

  // Build pairs within each gender group
  function makePairs(group: Competitor[]): Array<[Competitor | undefined, Competitor | undefined]> {
    const pairs: Array<[Competitor | undefined, Competitor | undefined]> = [];
    for (let i = 0; i < group.length; i += 2) {
      pairs.push([group[i], group[i + 1]]);
    }
    return pairs;
  }

  const malePairs   = makePairs(males);
  const femalePairs = makePairs(females);
  // Interleave: m, f, m, f … for a varied schedule
  const allPairs: Array<[Competitor | undefined, Competitor | undefined]> = [];
  const maxLen = Math.max(malePairs.length, femalePairs.length);
  for (let i = 0; i < maxLen; i++) {
    if (i < malePairs.length)   allPairs.push(malePairs[i]);
    if (i < femalePairs.length) allPairs.push(femalePairs[i]);
  }

  const qualRound1: QualSlot[] = allPairs.slice(0, R1_SLOTS.length).map((pair, i) =>
    makeQS(R1_SLOTS[i], pair[0]?.id ?? null, pair[1]?.id ?? null)
  );

  // R2: rotate within each gender group to avoid identical matchups, then interleave again
  function rotatedPairs(group: Competitor[]): Array<[Competitor | undefined, Competitor | undefined]> {
    if (group.length < 2) return makePairs(group);
    const evens = group.filter((_, i) => i % 2 === 0);
    const odds  = group.filter((_, i) => i % 2 !== 0);
    const rotated = odds.length > 1 ? [...odds.slice(1), odds[0]] : [...odds];
    return evens.map((e, i) => [e, rotated[i]]);
  }

  const maleR2   = rotatedPairs(males);
  const femaleR2 = rotatedPairs(females);
  const allR2: Array<[Competitor | undefined, Competitor | undefined]> = [];
  const maxR2 = Math.max(maleR2.length, femaleR2.length);
  for (let i = 0; i < maxR2; i++) {
    if (i < maleR2.length)   allR2.push(maleR2[i]);
    if (i < femaleR2.length) allR2.push(femaleR2[i]);
  }

  const qualRound2: QualSlot[] = allR2.slice(0, R2_SLOTS.length).map((pair, i) =>
    makeQS(R2_SLOTS[i], pair[0]?.id ?? null, pair[1]?.id ?? null)
  );

  return { qualRound1, qualRound2 };
}

// ─── persistence ─────────────────────────────────────────────────────────────

export function loadData(): SpeedCompetitionData {
  try {
    const s = localStorage.getItem(STORAGE_KEY);
    if (s) return JSON.parse(s) as SpeedCompetitionData;
  } catch { /* ignore parse errors */ }
  return getDefaultData();
}

export function saveData(data: SpeedCompetitionData): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export function resetData(): void {
  localStorage.removeItem(STORAGE_KEY);
}

// ─── formatting ──────────────────────────────────────────────────────────────

export function formatTime(r: RunResult): string {
  if (r.na)  return 'NA';
  if (r.dnf) return 'DNF';
  if (r.time === null) return '—';
  if (r.time < 60) return `${r.time.toFixed(2)}s`;
  const mins = Math.floor(r.time / 60);
  const secs = r.time - mins * 60;
  return `${mins}:${secs < 10 ? '0' : ''}${secs.toFixed(2)}`;
}

export function parseTimeInput(raw: string): RunResult {
  const s = raw.trim().toUpperCase();
  if (s === '' || s === '—') return { time: null, dnf: false, na: false };
  if (s === 'DNF' || s === 'D') return { time: null, dnf: true,  na: false };
  if (s === 'NA'  || s === 'N') return { time: null, dnf: false, na: true  };

  // Accept m:ss.xx or m:ss,xx  (e.g. 1:23.45 or 1:09,50)
  const colonMatch = s.match(/^(\d+):(\d{1,2}(?:[.,]\d{0,2})?)$/);
  if (colonMatch) {
    const mins = parseInt(colonMatch[1], 10);
    const secs = parseFloat(colonMatch[2].replace(',', '.'));
    if (!isNaN(secs) && secs >= 0 && secs < 60) {
      const total = Math.round((mins * 60 + secs) * 100) / 100;
      if (total > 0 && total < 9000) return { time: total, dnf: false, na: false };
    }
    return { time: null, dnf: false, na: false };
  }

  // Plain seconds: xx.xx or xx,xx  (e.g. 9.45)
  const n = parseFloat(s.replace(',', '.'));
  if (isNaN(n) || n <= 0 || n >= 9000) return { time: null, dnf: false, na: false };
  return { time: Math.round(n * 100) / 100, dnf: false, na: false };
}

export function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
}

// ─── rankings ────────────────────────────────────────────────────────────────

export interface AthleteQualResult {
  competitor: Competitor;
  run1: RunResult;
  run2: RunResult;
  bestTime: number | null;
  rank: number;
}

export interface GenderedRankings {
  men: AthleteQualResult[];
  women: AthleteQualResult[];
}

function sortAndRank(rows: AthleteQualResult[]): AthleteQualResult[] {
  rows.sort((a, b) => {
    if (a.bestTime !== null && b.bestTime !== null) return a.bestTime - b.bestTime;
    if (a.bestTime !== null) return -1;
    if (b.bestTime !== null) return 1;
    const aScore = (a.run1.dnf || a.run2.dnf) ? 0 : (a.run1.na || a.run2.na) ? 1 : 2;
    const bScore = (b.run1.dnf || b.run2.dnf) ? 0 : (b.run1.na || b.run2.na) ? 1 : 2;
    return aScore - bScore;
  });
  rows.forEach((r, i) => (r.rank = i + 1));
  return rows;
}

export function computeQualRankings(data: SpeedCompetitionData): AthleteQualResult[] {
  const map = new Map<AthleteId, { run1: RunResult; run2: RunResult }>(
    data.competitors.map(c => [c.id, { run1: emptyResult(), run2: emptyResult() }])
  );

  data.qualRound1.forEach(slot => {
    if (slot.leftId  && map.has(slot.leftId))  map.get(slot.leftId)!.run1  = slot.leftResult;
    if (slot.rightId && map.has(slot.rightId)) map.get(slot.rightId)!.run1 = slot.rightResult;
  });
  data.qualRound2.forEach(slot => {
    if (slot.leftId  && map.has(slot.leftId))  map.get(slot.leftId)!.run2  = slot.leftResult;
    if (slot.rightId && map.has(slot.rightId)) map.get(slot.rightId)!.run2 = slot.rightResult;
  });

  const rows: AthleteQualResult[] = data.competitors.map(c => {
    const r = map.get(c.id)!;
    const times: number[] = [];
    if (!r.run1.dnf && !r.run1.na && r.run1.time !== null) times.push(r.run1.time);
    if (!r.run2.dnf && !r.run2.na && r.run2.time !== null) times.push(r.run2.time);
    return { competitor: c, run1: r.run1, run2: r.run2, bestTime: times.length ? Math.min(...times) : null, rank: 0 };
  });

  return sortAndRank(rows);
}

/** Returns separate ranked lists for men and women. */
export function computeGenderedRankings(data: SpeedCompetitionData): GenderedRankings {
  const all = computeQualRankings(data);
  const men   = sortAndRank(all.filter(r => r.competitor.gender === 'male').map(r => ({ ...r })));
  const women = sortAndRank(all.filter(r => r.competitor.gender === 'female').map(r => ({ ...r })));
  return { men, women };
}

// ─── bracket logic ────────────────────────────────────────────────────────────

/**
 * Numeric score for a run result (lower = better):
 *   real time < DNF (9000) < NA (9001) < no result (9002)
 */
function runScore(r: RunResult): number {
  if (!r.dnf && !r.na && r.time !== null) return r.time;
  if (r.dnf) return 9000;
  if (r.na)  return 9001;
  return 9002;
}

export function getMatchWinner(
  m: FinalsMatch,
  qualRankMap?: Map<AthleteId, number>,
): AthleteId | null {
  if (!m.leftId && !m.rightId) return null;
  if (!m.leftId) return m.rightId;
  if (!m.rightId) return m.leftId;
  const ls = runScore(m.leftResult);
  const rs = runScore(m.rightResult);
  // If neither has a real result yet (both >= 9000) we need at least one
  // of them to have something entered before declaring a winner
  const bothEmpty = ls >= 9002 && rs >= 9002;
  if (bothEmpty) return null;
  if (ls !== rs) return ls < rs ? m.leftId : m.rightId;
  // Equal scores (both DNF, both NA, or identical times) → qual rank tiebreaker
  if (qualRankMap) {
    const lr = qualRankMap.get(m.leftId)  ?? Infinity;
    const rr = qualRankMap.get(m.rightId) ?? Infinity;
    if (lr !== rr) return lr < rr ? m.leftId : m.rightId;
  }
  return null;
}

export function getMatchLoser(
  m: FinalsMatch,
  qualRankMap?: Map<AthleteId, number>,
): AthleteId | null {
  const winner = getMatchWinner(m, qualRankMap);
  if (!winner || !m.leftId || !m.rightId) return null;
  return winner === m.leftId ? m.rightId : m.leftId;
}

/** Keep competitors and pairings; clear all run results. */
export function clearTimes(data: SpeedCompetitionData): SpeedCompetitionData {
  const cs = (s: QualSlot):    QualSlot    => ({ ...s, leftResult: emptyResult(), rightResult: emptyResult() });
  const cm = (m: FinalsMatch): FinalsMatch => ({ ...m, leftResult: emptyResult(), rightResult: emptyResult() });
  return {
    ...data,
    qualRound1:        data.qualRound1.map(cs),
    qualRound2:        data.qualRound2.map(cs),
    mensQuarterFinals: data.mensQuarterFinals.map(cm),
    mensSemiFinals:    data.mensSemiFinals.map(cm),
    mensFinals:        data.mensFinals.map(cm),
    womensSemiFinals:  data.womensSemiFinals.map(cm),
    womensFinals:      data.womensFinals.map(cm),
  };
}

/**
 * Returns the full data with all bracket IDs populated based on qual rankings
 * and previous round results. Preserves existing run results.
 */
export function autoPopulateBracket(data: SpeedCompetitionData): SpeedCompetitionData {
  const { men, women } = computeGenderedRankings(data);

  const mensMap   = new Map(men.map(r   => [r.competitor.id, r.rank]));
  const womensMap = new Map(women.map(r => [r.competitor.id, r.rank]));

  const mByRank = (r: number): AthleteId | null => men.find(x   => x.rank === r)?.competitor.id ?? null;
  const wByRank = (r: number): AthleteId | null => women.find(x => x.rank === r)?.competitor.id ?? null;

  const mw = (m: FinalsMatch) => getMatchWinner(m, mensMap);
  const ww = (m: FinalsMatch) => getMatchWinner(m, womensMap);
  const ml = (m: FinalsMatch) => getMatchLoser(m, mensMap);
  const wl = (m: FinalsMatch) => getMatchLoser(m, womensMap);

  // Men's QF: MT1 vs MT8, MT2 vs MT7, MT3 vs MT6, MT4 vs MT5
  const mensQF = data.mensQuarterFinals.map((m, i) => ({
    ...m, leftId: mByRank(i + 1), rightId: mByRank(8 - i),
  }));

  // Men's SF: MQ2 vs MQ3, MQ1 vs MQ4
  const mensSF: FinalsMatch[] = [
    { ...data.mensSemiFinals[0], leftId: mw(mensQF[1]), rightId: mw(mensQF[2]) },
    { ...data.mensSemiFinals[1], leftId: mw(mensQF[0]), rightId: mw(mensQF[3]) },
  ];

  // Men's Finals: bronze = losers, gold = winners
  const mensFin: FinalsMatch[] = [
    { ...data.mensFinals[0], leftId: ml(mensSF[0]), rightId: ml(mensSF[1]) },
    { ...data.mensFinals[1], leftId: mw(mensSF[0]), rightId: mw(mensSF[1]) },
  ];

  // Women's SF: WT1 vs WT4, WT2 vs WT3
  const womensSF: FinalsMatch[] = [
    { ...data.womensSemiFinals[0], leftId: wByRank(1), rightId: wByRank(4) },
    { ...data.womensSemiFinals[1], leftId: wByRank(2), rightId: wByRank(3) },
  ];

  // Women's Finals: bronze = losers, gold = winners
  const womensFin: FinalsMatch[] = [
    { ...data.womensFinals[0], leftId: wl(womensSF[0]), rightId: wl(womensSF[1]) },
    { ...data.womensFinals[1], leftId: ww(womensSF[0]), rightId: ww(womensSF[1]) },
  ];

  return {
    ...data,
    mensQuarterFinals: mensQF,
    mensSemiFinals:    mensSF,
    mensFinals:        mensFin,
    womensSemiFinals:  womensSF,
    womensFinals:      womensFin,
  };
}
